// components/form/fields/DisplayField.tsx
import { useFormContext } from "react-hook-form";
import { type FormField } from "../formFieldType";
import MyBox from "../../MyBox";
import { Copy, Check, Eye, Info } from "lucide-react";
import { useState } from "react";

interface DisplayFieldProps {
  field: FormField;
  formData?: Record<string, any>;
}

export function DisplayField({ field, formData }: DisplayFieldProps) {
  const { getValues } = useFormContext();
  const [copied, setCopied] = useState(false);

  // Get the value from various sources
  const getValue = () => {
    if (field.value !== undefined) return field.value;
    if (formData?.[field.name] !== undefined) return formData[field.name];
    return getValues(field.name);
  };

  const value = getValue();
  const hasValue = value !== undefined && value !== null && value !== "";

  // Format value based on type
  const formatValue = (val: any): string => {
    if (val === undefined || val === null) return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (val instanceof Date) return val.toLocaleDateString();
    if (Array.isArray(val)) {
      if (val.length === 0) return "-";
      return val.map(v => typeof v === 'object' ? JSON.stringify(v) : v).join(", ");
    }
    if (typeof val === "object") {
      try {
        return JSON.stringify(val, null, 2);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  // Handle copy to clipboard
  const handleCopy = async () => {
    if (!hasValue) return;
    try {
      const textToCopy = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Determine if value is long (for truncation)
  const isLongValue = typeof value === 'string' && value.length > 100;
  const [isExpanded, setIsExpanded] = useState(false);
  const displayValue = isLongValue && !isExpanded 
    ? value.substring(0, 100) + "..."
    : value;

  return (
    <MyBox topText={field.label} required={field.required}>
      <div className="w-full space-y-2">
        {/* Display Container */}
        <div 
          className={`
            relative w-full
            border rounded-lg
            transition-all duration-200
            ${field.error 
              ? "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10" 
              : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
            }
            ${field.copyable ? "pr-10" : ""}
            ${field.clickable ? "cursor-pointer hover:shadow-md transition-shadow" : ""}
          `}
          onClick={() => field.clickable && field.onClick?.(value)}
        >
          {/* Content */}
          <div className="p-3">
            {field.renderCustom ? (
              // Custom render function
              <div className="text-gray-900 dark:text-gray-100">
                {field.renderCustom({ ...getValues(), ...formData })}
              </div>
            ) : (
              <div className="space-y-1">
                {/* Icon and Value */}
                <div className="flex items-start gap-2">
                  {field.icon && (
                    <div className="flex-shrink-0 mt-0.5">
                      {typeof field.icon === 'string' ? (
                        <span className="text-gray-400 dark:text-gray-500">{field.icon}</span>
                      ) : (
                        field.icon
                      )}
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    {!hasValue ? (
                      <span className="text-gray-400 dark:text-gray-500 italic">
                        {field.emptyText || "Not provided"}
                      </span>
                    ) : (
                      <>
                        {field.format === "json" ? (
                          <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">
                            {formatValue(value)}
                          </pre>
                        ) : field.format === "currency" ? (
                          <span className="text-gray-900 dark:text-white font-semibold">
                            {new Intl.NumberFormat('en-US', { 
                              style: 'currency', 
                              currency: field.currency || 'USD' 
                            }).format(Number(value))}
                          </span>
                        ) : field.format === "percentage" ? (
                          <span className="text-gray-900 dark:text-white">
                            {Number(value).toFixed(field.decimals || 0)}%
                          </span>
                        ) : field.format === "date" ? (
                          <span className="text-gray-900 dark:text-white">
                            {new Date(value).toLocaleDateString()}
                          </span>
                        ) : field.format === "datetime" ? (
                          <span className="text-gray-900 dark:text-white">
                            {new Date(value).toLocaleString()}
                          </span>
                        ) : field.format === "phone" ? (
                          <a 
                            href={`tel:${value}`}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {formatValue(value)}
                          </a>
                        ) : field.format === "email" ? (
                          <a 
                            href={`mailto:${value}`}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {formatValue(value)}
                          </a>
                        ) : field.format === "url" ? (
                          <a 
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                          >
                            {formatValue(value)}
                          </a>
                        ) : (
                          <div className="text-gray-900 dark:text-gray-100 break-words">
                            {isLongValue ? displayValue : formatValue(value)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Description */}
                {field.description && (
                  <div className="flex items-start gap-2 mt-1">
                    <Info className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {field.description}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Copy Button */}
          {field.copyable && hasValue && (
            <button
              type="button"
              onClick={handleCopy}
              className="absolute right-2 top-2 p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <Copy className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              )}
            </button>
          )}

          {/* Expand/Collapse for long text */}
          {isLongValue && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="absolute bottom-2 right-2 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400"
            >
              {isExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>

        {/* Badges/Tags for array values */}
        {field.showAsTags && Array.isArray(value) && value.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {value.map((item, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                {item}
              </span>
            ))}
          </div>
        )}

        {/* Helper Text */}
        {field.helperText && !field.error && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {field.helperText}
          </p>
        )}

        {/* Error Message */}
        {field.error && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            {field.error}
          </p>
        )}
      </div>
    </MyBox>
  );
}